import React, { useRef, useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  Button,
  IconButton,
  Refresh,
  Copy,
  Trash,
  Edit,
  Exit,
  More,
  Expand,
  Menu
  // @ts-ignore
} from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { Theme } from 'features/ui/types'
import PdfExportButton from 'pages/dashboard/main/pdfExport/PdfExportButton'
import ReportButton from 'pages/dashboard/main/ReportButton'
import { useResizeObserver } from 'hooks/useResizeObserver'
import { useDashboardData } from 'hooks/useDashboardState'
import DarkModeToggle from 'pages/dashboard/DarkModeToggle'
import { updateRefreshTimeStamp } from 'features/ui/uiSlice'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  selectV4ScheduledReportInfo,
  selectIsScheduledReportConfigModalOpen
} from 'features/scheduledReport/selectors'
import LiveReportConfigModal from 'pages/dashboard/main/LiveReportConfigModal'
import { openLiveReportConfigModal } from 'features/liveReportConfig/liveReportConfigSlice'
import { useAccountId } from 'hooks/usePageConfig'
import AlwaysActiveTooltip from 'components/share/AlwaysActiveTooltip'
import { SystemTheme } from 'hooks/useUiState/types'
import { heapTrack, HeapEvent, HeapEventPage } from 'utils/heapUtils'
import { sendTypedMessage } from 'utils/iframeUtil'

import { openScheduledReportConfigModal } from 'features/scheduledReport/scheduledReportSlice'
import ScheduledReportConfigModal from 'pages/dashboard/main/ScheduledReportConfigModal'
import ReportSubscribeButton from 'pages/dashboard/main/ReportSubscribeButton'
import ReportUnsubscriptionModal from 'pages/dashboard/main/ReportUnSubscriptionModal'
import ReportSubscriptionModal from 'pages/dashboard/main/ReportSubscriptionModal'
import { useBooleanVariationFromFeatureFlag } from 'hooks/useBooleanVariationFromFeatureFlag'
import {
  SCHEDULED_REPORT_BUTTON_FLAG,
  EDIT_REDIRECT_FEATURE_FLAG,
  DELETE_COPY_FEATURE_FLAG
} from 'app/featureFlags'
import Breadcrumbs from '../Breadcrumbs'
import MoreOptionsMenuItem from './MoreOptionsMenuItem'
import useActionPermissions from '../hooks/useActionPermissions'
import { RESPONSIVE_ACTION_LIST_LAYOUT_BREAKPOINT } from './constants'
import AvailableSoonTooltipContent from './AvailableSoonTooltipContent'
import { useSetPresentationModeUrl, useExitNewDashboardMode } from '../hooks'
import DeleteConfirmationDialog from './DeleteConfirmationDialog'
import { useDuplicateDashboard } from '../hooks/useDuplicateDashboard'
import ErrorDialog from './ErrorDialog'

const Container = styled.div<{ mode: Theme }>`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 12px 20px;
  gap: 8px;
  background-color: ${({ mode }) => sys[mode].elevation[100].background};
  border-bottom: 1px solid ${({ mode }) => sys[mode].common.divider[100]};
  box-sizing: border-box;
`

const StyledButton = styled(Button)`
  white-space: nowrap;
`

const buttonActiveStyle = css<{
  $theme: SystemTheme
}>`
  && {
    background-color: ${({ $theme }) => $theme.interaction.secondary.pressed};
  }
`

const StyledIconButton = styled(IconButton)<{
  $theme?: SystemTheme
  $isMenuOpen?: boolean
}>`
  min-width: 32px;
  ${({ $isMenuOpen }) => $isMenuOpen && buttonActiveStyle}
`

const Divider = styled.div<{ mode: Theme }>`
  background: ${({ mode }) => sys[mode].common.divider[100]};
  height: 1px;
  margin: 4px 0;
`

interface ActionListProps {
  className?: string
  onPdfExport: () => void
}

const ActionList = ({ className, onPdfExport }: ActionListProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const containerRef = useRef<HTMLDivElement>(null)
  const ellipsisButtonRef = useRef<HTMLDivElement>(null)

  const mode = useDisplayTheme() as Theme
  const theme = useSysTheme()
  const { dashboardId } = useParams()
  const dashboardData = useDashboardData()

  const [showEllipsisButton, setShowEllipsisButton] = useState(false)
  const [openMenu, setOpenMenu] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const {
    copyPermission,
    reportPermission,
    deletePermission,
    editPermission,
    pdfExportPermission
  } = useActionPermissions()
  const accountId = useAccountId()
  const setPresentationModeUrl = useSetPresentationModeUrl()
  const exitNewDashboardMode = useExitNewDashboardMode()
  const hasShowScheduledReportButtonPermission = useBooleanVariationFromFeatureFlag(
    SCHEDULED_REPORT_BUTTON_FLAG
  )
  const hasExperimentEditFeature = useBooleanVariationFromFeatureFlag(EDIT_REDIRECT_FEATURE_FLAG)
  const hasExperimentCopyDeleteFeature = useBooleanVariationFromFeatureFlag(
    DELETE_COPY_FEATURE_FLAG
  )

  const areReportPrerequisitesReady = dashboardId && accountId
  const scheduledReportInfo = useAppSelector(selectV4ScheduledReportInfo)

  const isScheduledReportConfigModalOpen = useAppSelector(selectIsScheduledReportConfigModalOpen)

  const { handleDuplicate, isDuplicating } = useDuplicateDashboard({
    dashboardId,
    dashboardName: dashboardData?.name
  })

  const dashboardError = useAppSelector(({ dashboard }) => dashboard.error)

  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)

  const updateEllipsisButtonVisibility = (observeEntry: ResizeObserverEntry[]) => {
    const containerWidth = observeEntry[0].contentRect.width
    setShowEllipsisButton(containerWidth < RESPONSIVE_ACTION_LIST_LAYOUT_BREAKPOINT)
  }

  const handleRefresh = () => {
    dispatch(updateRefreshTimeStamp())
    heapTrack(HeapEvent.V4DahboardRefeshClick)
  }

  useResizeObserver(containerRef, debounce(updateEllipsisButtonVisibility, 10))

  useEffect(() => {
    if (dashboardError) {
      setIsErrorDialogOpen(true)
    }
  }, [dashboardError])

  useEffect(() => {
    if (!showEllipsisButton) {
      setOpenMenu(false)
    }
  }, [showEllipsisButton])

  return (
    <>
      <Container className={className} ref={containerRef} mode={mode}>
        <Breadcrumbs
          items={[
            {
              title: t('dashboard_other'),
              route: '/dashboard/home',
              onClick: () => sendTypedMessage('v4DashboardHome')
            },
            ...(dashboardData?.name ? [{ title: dashboardData.name }] : [])
          ]}
        />
        {!showEllipsisButton && <DarkModeToggle page={HeapEventPage.DashboardView} />}
        {pdfExportPermission && <PdfExportButton handleExport={onPdfExport} />}
        {reportPermission && areReportPrerequisitesReady && (
          <>
            <ReportButton
              onLiveReportClick={() => dispatch(openLiveReportConfigModal())}
              onScheduledReportClick={() => dispatch(openScheduledReportConfigModal())}
            />
            <LiveReportConfigModal dashboardId={Number(dashboardId)} />
            <ScheduledReportConfigModal
              key={`${dashboardId}-${isScheduledReportConfigModalOpen}`}
            />
          </>
        )}
        {!editPermission && !!scheduledReportInfo && hasShowScheduledReportButtonPermission && (
          <>
            <ReportSubscribeButton />
            <ReportUnsubscriptionModal dashboardId={Number(dashboardId)} />
            <ReportSubscriptionModal dashboardId={Number(dashboardId)} />
          </>
        )}
        {showEllipsisButton && (
          <>
            <StyledIconButton
              mode={mode}
              color="secondary"
              variant="contained"
              ref={ellipsisButtonRef}
              $theme={theme}
              $isMenuOpen={openMenu}
              onClick={() => setOpenMenu(true)}>
              <More mode={mode} />
            </StyledIconButton>
            <Menu
              mode={mode}
              open={openMenu}
              anchorEl={ellipsisButtonRef.current}
              placement="bottom-end"
              onClose={() => {
                setOpenMenu(false)
              }}>
              {copyPermission && (
                <AlwaysActiveTooltip
                  content={
                    <AvailableSoonTooltipContent
                      i18nKey="duplicateWillBeAvaialbleSoon"
                      onClick={exitNewDashboardMode}
                    />
                  }
                  placement="left"
                  enabled={!hasExperimentCopyDeleteFeature}
                  interactive
                  offset={[0, 0]}>
                  <MoreOptionsMenuItem
                    StartIcon={Copy}
                    disabled={!hasExperimentCopyDeleteFeature || isDuplicating}
                    onClick={handleDuplicate}>
                    {t('duplicate')}
                  </MoreOptionsMenuItem>
                </AlwaysActiveTooltip>
              )}
              <MoreOptionsMenuItem
                StartIcon={Refresh}
                onClick={() => {
                  handleRefresh()
                  setOpenMenu(false)
                }}>
                {t('refresh')}
              </MoreOptionsMenuItem>
              {deletePermission && (
                <AlwaysActiveTooltip
                  content={
                    <AvailableSoonTooltipContent
                      i18nKey="deleteWillBeAvaialbleSoon"
                      onClick={exitNewDashboardMode}
                    />
                  }
                  placement="left"
                  enabled={!hasExperimentCopyDeleteFeature}
                  interactive
                  offset={[0, 0]}>
                  <MoreOptionsMenuItem
                    StartIcon={Trash}
                    disabled={!hasExperimentCopyDeleteFeature}
                    onClick={() => setIsDeleteDialogOpen(true)}>
                    {t('delete')}
                  </MoreOptionsMenuItem>
                </AlwaysActiveTooltip>
              )}
              <Divider mode={mode} />
              <MoreOptionsMenuItem>
                <DarkModeToggle page={HeapEventPage.DashboardView} />
              </MoreOptionsMenuItem>
            </Menu>
          </>
        )}
        {!showEllipsisButton && (
          <AlwaysActiveTooltip content={t('refresh')} placement="bottom">
            <StyledIconButton
              mode={mode}
              color="secondary"
              variant="contained"
              onClick={handleRefresh}>
              <Refresh mode={mode} />
            </StyledIconButton>
          </AlwaysActiveTooltip>
        )}
        {copyPermission && !showEllipsisButton && (
          <AlwaysActiveTooltip
            content={
              !hasExperimentCopyDeleteFeature ? (
                <AvailableSoonTooltipContent
                  i18nKey="duplicateWillBeAvaialbleSoon"
                  onClick={exitNewDashboardMode}
                />
              ) : (
                t('duplicate')
              )
            }
            placement="bottom"
            interactive>
            <StyledIconButton
              mode={mode}
              color="secondary"
              variant="contained"
              disabled={!hasExperimentCopyDeleteFeature || isDuplicating}
              onClick={handleDuplicate}>
              <Copy mode={mode} />
            </StyledIconButton>
          </AlwaysActiveTooltip>
        )}
        {deletePermission && !showEllipsisButton && (
          <AlwaysActiveTooltip
            content={
              !hasExperimentCopyDeleteFeature ? (
                <AvailableSoonTooltipContent
                  i18nKey="deleteWillBeAvaialbleSoon"
                  onClick={exitNewDashboardMode}
                />
              ) : (
                t('delete')
              )
            }
            placement="bottom-end"
            interactive>
            <StyledIconButton
              mode={mode}
              color="secondary"
              variant="contained"
              disabled={!hasExperimentCopyDeleteFeature}
              onClick={() => setIsDeleteDialogOpen(true)}>
              <Trash mode={mode} />
            </StyledIconButton>
          </AlwaysActiveTooltip>
        )}
        {editPermission && (
          <AlwaysActiveTooltip
            content={
              <AvailableSoonTooltipContent
                i18nKey="editWillBeAvaialbleSoon"
                onClick={exitNewDashboardMode}
              />
            }
            placement="bottom-end"
            interactive
            enabled={!hasExperimentEditFeature}
            offset={[0, 0]}>
            <StyledButton
              mode={mode}
              color="primary"
              startIcon={<Edit mode={mode} />}
              disabled={!hasExperimentEditFeature}
              onClick={() => sendTypedMessage('goToEdit', dashboardId)}>
              {t('edit')}
            </StyledButton>
          </AlwaysActiveTooltip>
        )}
        <StyledButton
          mode={mode}
          color="secondary"
          startIcon={<Exit mode={mode} />}
          onClick={() => {
            exitNewDashboardMode()
            heapTrack(HeapEvent.V4ExitNewDashboardClick, { page: HeapEventPage.DashboardView })
          }}>
          {t('exitNewDashboardMode')}
        </StyledButton>

        <StyledIconButton
          mode={mode}
          color="primary"
          variant="contained"
          onClick={() => {
            setPresentationModeUrl(true)
            heapTrack(HeapEvent.V4PresentationModeClick, { isPresentationMode: true })
          }}>
          <Expand mode={mode} />
        </StyledIconButton>
      </Container>
      <DeleteConfirmationDialog
        dashboardId={dashboardId!}
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      />
      <ErrorDialog
        errorInfo={dashboardError}
        open={isErrorDialogOpen}
        onClose={() => setIsErrorDialogOpen(false)}
      />
    </>
  )
}

export default ActionList
